import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Gantt, Task, ViewMode } from "gantt-task-react";
import dayjs from "dayjs";

import "gantt-task-react/dist/index.css";
import "@/styles/gantt.css";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import KanbanDrawerContent from "@/routes/(planner-screens)/components/kanban/kanban-drawer-content";

import { useQueryParams } from "@/hooks/use-query-params";
import { useGetData, usePutData } from "@/hooks/services/use-query-hooks";
import {
  JobOrderList,
  UpdateJobOrder,
} from "@/hooks/services/pps/handlers/job-orders-api";
import {
  JobResponse,
  Job,
  JobPayload,
} from "@/hooks/services/pps/types/job-orders.type";

import { defaultSelectedItem, ViewPps } from "../utils/constants";

import { getStartEndDateForProject, transformTask } from "../utils/utils";

import GanttChartLoading from "../components/loading";
import {
  parseFiltersFromQuery,
  parseSortsFromQuery,
  type DynamicFilter,
  type DynamicSort,
} from "@/components/search-filter-bar";

interface DrawerItem {
  show: boolean;
  task: Job;
}

export const Route = createFileRoute("/(planner-screens)/planner/gantt")({
  component: RouteComponent,
});

function RouteComponent() {
  const { params } = useQueryParams();

  // Read filters and sorts from URL search params (managed by parent planner.tsx)
  const dynamicFilters: DynamicFilter[] = params.filters
    ? parseFiltersFromQuery(params.filters as string)
    : [];

  const dynamicSorts: DynamicSort[] = params.sorts
    ? parseSortsFromQuery(params.sorts as string)
    : [];

  const { data, isLoading } = useGetData<JobResponse>(
    JobOrderList({
      ...params,
      page: 1,
      pageSize: 100,
      viewId: ViewPps.gantt.id,
      ...(dynamicSorts.length > 0 && {
        encodeQuery: { sorts: params.sorts },
      }),
      ...(dynamicFilters.length > 0 && {
        encodeQuery: { filters: params.filters },
      }),
    }),
  );

  const { mutate } = usePutData<JobPayload, any>(UpdateJobOrder());

  const [tasks, setTasks] = useState<Task[]>([]);
  const [view, setView] = useState<ViewMode>(ViewMode.Day);
  const [isChecked, setIsChecked] = useState<boolean>(true);
  const [isRendered, setIsRendered] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<DrawerItem>({
    show: false,
    task: defaultSelectedItem,
  });

  let columnWidth = 50;
  let containerWidth = "";
  if (view === ViewMode.HalfDay) {
    columnWidth = 100;
    containerWidth = "aero:w-[1200px]";
  } else if (view === ViewMode.Day) {
    containerWidth = "aero:w-[1000px]";
  } else if (view === ViewMode.Month) {
    columnWidth = 150;
    containerWidth = "aero:w-full";
  } else if (view === ViewMode.Week) {
    columnWidth = 250;
    containerWidth = "aero:w-3/4";
  } else if (view === ViewMode.Year) {
    containerWidth = "aero:w-full";
  }

  useEffect(() => {
    if (data?.data) {
      const filtersDate = data.data.filter(
        (item: any) => item?.startDate && item?.dueDate,
      );

      const transformedTask =
        filtersDate.length > 0
          ? filtersDate
              .map((job: Job) => transformTask(job))
              .filter((task: any): task is Task => task !== null)
          : [];

      setTasks(transformedTask);
      setIsRendered(true);
    } else {
      setTasks([]);
      setIsRendered(true);
    }
  }, [data]);

  useEffect(() => {
    if (isRendered) {
      const todayElement = document.querySelector(".today");
      if (todayElement) {
        todayElement.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "nearest",
        });
      }
    }
  }, [isRendered]);

  const handleTaskChange = (updatedTask: Task) => {
    setTasks((prevTasks) => {
      let updatedTasks = prevTasks.map((task) =>
        task.id === updatedTask.id ? updatedTask : task,
      );

      if (updatedTask.project) {
        const [newStart, newEnd] = getStartEndDateForProject(
          updatedTasks,
          updatedTask.project,
        );

        updatedTasks = updatedTasks.map((task) => {
          if (task.id === updatedTask.id) {
            return {
              ...task,
              start: newStart,
              end: newEnd,
            };
          }
          return task;
        });
      }
      return updatedTasks;
    });

    handleUpdateTask(updatedTask);
  };

  const handleUpdateTask = (formData: Task) => {
    const payload = {
      startDate: dayjs(formData?.start).format("YYYY-MM-DD"),
      dueDate: dayjs(formData.end).format("YYYY-MM-DD"),
    };

    mutate({
      jobId: parseInt(formData.id, 10),
      jobData: payload,
    } as JobPayload);
  };

  const handleTaskDelete = (task: Task) => {
    const conf = window.confirm("Are you sure about " + task.name + " ?");
    if (conf) {
      setTasks(tasks.filter((t) => t.id !== task.id));
    }
    return conf;
  };

  const handleProgressChange = async (task: Task) => {
    setTasks(tasks.map((t) => (t.id === task.id ? task : t)));
  };

  const handleDblClick = (task: Task) => {
    // currently the id from task is assign with job_order_no

    setSelectedItem((prev) => ({
      show: !prev.show,
      task:
        data?.data.find((f: any) => f.id === parseInt(task.id, 10)) ||
        defaultSelectedItem,
    }));
  };

  const handleExpanderClick = (task: Task) => {
    setTasks(tasks.map((t) => (t.id === task.id ? task : t)));
  };

  if (isLoading) {
    return <GanttChartLoading />;
  }

  return (
    <div className="aero:flex aero:flex-col aero:gap-4">
      {/* Subtitle */}
      <div className="aero:flex aero:items-center aero:gap-2">
        <span className="aero:w-[3px] aero:h-7 aero:bg-brand-primary-600 aero:rounded-full" />
        <h2 className="aero:font-bold aero:text-lg aero:leading-7 aero:text-brand-primary-600">
          Gantt Chart
        </h2>
      </div>

      <div className={`${containerWidth}`}>
        <Card>
          <CardContent>
            {/* Toolbar */}
            <div className="aero:flex aero:items-center aero:justify-between aero:space-x-4 aero:mb-2 aero:p-4 aero:!rounded-lg aero:bg-card">
              <div className="aero:flex aero:items-center aero:space-x-2">
                <Select onValueChange={(value) => setView(value as ViewMode)}>
                  <SelectTrigger className="aero:w-[180px]">
                    <SelectValue placeholder="Select view" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ViewMode.HalfDay}>Half Day</SelectItem>
                    <SelectItem value={ViewMode.Day}>Day</SelectItem>
                    <SelectItem value={ViewMode.Week}>Week</SelectItem>
                    <SelectItem value={ViewMode.Month}>Month</SelectItem>
                    <SelectItem value={ViewMode.Year}>Year</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="aero:flex aero:items-center aero:space-x-2">
                <Switch
                  id="show-task-list"
                  checked={isChecked}
                  onCheckedChange={setIsChecked}
                />
                <Label htmlFor="show-task-list">Show Task List</Label>
              </div>
            </div>
            {tasks?.length === 0 ? (
              <Card className="aero:w-full aero:flex aero:justify-center aero:items-center aero:h-60">
                <CardContent>Currently no data to display</CardContent>
              </Card>
            ) : (
              <Gantt
                tasks={tasks || []}
                viewMode={view}
                onDateChange={handleTaskChange}
                onDelete={handleTaskDelete}
                onProgressChange={handleProgressChange}
                onDoubleClick={handleDblClick}
                // onSelect={handleSelect}
                TooltipContent={Tooltip}
                onExpanderClick={handleExpanderClick}
                listCellWidth={isChecked ? "155px" : ""}
                columnWidth={columnWidth}
              />
            )}
          </CardContent>
        </Card>
      </div>

      <KanbanDrawerContent
        open={selectedItem.show}
        onOpenChange={(open) =>
          setSelectedItem((prev) => ({
            ...prev,
            show: open,
          }))
        }
        data={selectedItem.task}
        viewMode
      />
    </div>
  );
}

/**
 * This Tooltip is used for Gantt Chart custom tooltip only
 * @param task
 * @returns
 */
const Tooltip = ({ task }: { task: Task }) => (
  <Card className="aero:shadow-lg aero:border-none">
    <CardContent className="aero:p-3 aero:space-y-1">
      <p className="aero:!font-medium aero:!text-sm">{task.name}</p>
      <div className="aero:!text-xs aero:space-y-0.5 aero:!text-muted-foreground">
        <p>Start: {dayjs(task?.start).format("MM/DD/YYYY")}</p>
        <p>End: {dayjs(task.end).format("MM/DD/YYYY")}</p>
        <p>Progress: {task.progress || 0}%</p>
      </div>
    </CardContent>
  </Card>
);
